"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, FileCheck2, ThermometerSnowflake, Scale, Download, CircleAlert as AlertCircle, ExternalLink } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const hplcStandards = [{
  parameter: "Purity by RP-HPLC",
  specification: ">= 98.0% (standard) / >= 99.0% (analytical)",
  method: "C18 reversed-phase, gradient acetonitrile / 0.1% TFA"
}, {
  parameter: "Identity confirmation",
  specification: "Observed mass matches theoretical [M+H]+ within +/- 1.0 Da",
  method: "ESI-MS (Electrospray Ionization Mass Spectrometry)"
}, {
  parameter: "Trifluoroacetic acid (TFA)",
  specification: "<= 1.0% residual content (acetate exchange optional)",
  method: "Ion exchange chromatography / Headspace GC"
}, {
  parameter: "Moisture content (Karl Fischer)",
  specification: "<= 5.0% w/w lyophilized powder",
  method: "Coulometric Karl Fischer titration"
}, {
  parameter: "Solubility verification",
  specification: "Complete dissolution in sterile bacteriostatic water",
  method: "Visual inspection at 1.0 mg/mL concentration"
}];
const batchTraceabilitySteps = [{
  title: "1. Unique lot allocation",
  desc: "Every synthesis batch receives an encrypted 10-digit lot identifier linked to raw amino acid provenance records."
}, {
  title: "2. Analytical assay confirmation",
  desc: "Aliquots are extracted post-lyophilization for independent HPLC and mass spectrometry scans before vial sealing."
}, {
  title: "3. Certificate generation & storage",
  desc: "Raw chromatograms, spectra, and net peptide weight calculations are permanently indexed in our digital archive."
}, {
  title: "4. Rapid researcher retrieval",
  desc: "Enter your lot code in our portal to download the complete analytical dossier matching your vial."
}];
export default function Compliance() {
  const prefersReduced = useReducedMotion();
  const motionProps = prefersReduced ? {} : {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.35,
      ease: "easeOut"
    }
  };
  return <section id="quality-standards-compliance" data-nav="light" className="bg-background text-foreground border-b border-border py-14 md:py-20">
      <div className="mx-auto max-w-[1088px] px-6">
        {/* Section Header */}
        <motion.div {...motionProps} className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-px w-6 bg-primary" />
            <span className="font-mono text-xs font-semibold tracking-widest text-muted-foreground uppercase">
              VERIFICATION PROTOCOLS
            </span>
            <span className="h-px w-6 bg-primary" />
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground">
            Quality standards & compliance
          </h2>
          <p className="mt-3 text-base md:text-lg text-foreground/80 max-w-2xl mx-auto">
            Review our analytical release specifications, cold-chain transport parameters, and research-use verification guidelines.
          </p>
        </motion.div>

        {/* Accordion Stack */}
        <motion.div {...motionProps} className="border border-border rounded-md bg-card shadow-xs divide-y divide-border overflow-hidden">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
            {/* 1. Analytical Purity Testing */}
            <AccordionItem value="item-1" className="border-none px-5 md:px-7">
              <AccordionTrigger className="hover:no-underline py-5 text-left group">
                <div className="flex items-center gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-primary border border-border">
                    <ShieldCheck className="size-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-base text-foreground group-hover:text-primary transition-colors">
                      Analytical purity testing methodology (HPLC & MS)
                    </span>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">
                      RP-HPLC chromatograms and electrospray ionization mass spectrometry
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-6">
                <div className="rounded-md bg-muted/60 border border-border p-4 md:p-5">
                  <p className="text-sm text-foreground/90 leading-relaxed mb-4">
                    Every synthesized batch must meet or exceed a minimum threshold of 98.0% purity as determined by reversed-phase high-performance liquid chromatography. We do not release peptide lots with unverified impurities or ambiguous mass spectral peaks.
                  </p>

                  <div className="border border-border rounded-md bg-card overflow-hidden">
                    <Table>
                      <TableHeader className="bg-muted">
                        <TableRow className="border-border">
                          <TableHead className="font-mono text-xs text-foreground font-semibold">Testing parameter</TableHead>
                          <TableHead className="font-mono text-xs text-foreground font-semibold">Release specification</TableHead>
                          <TableHead className="font-mono text-xs text-foreground font-semibold hidden md:table-cell">Analytical method</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {hplcStandards.map((item, i) => <TableRow key={item.parameter} data-index={i} className="border-border hover:bg-muted/40">
                            <TableCell className="font-medium text-xs md:text-sm text-foreground">
                              {item.parameter}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-foreground/90">
                              {item.specification}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground hidden md:table-cell">
                              {item.method}
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
                    <span className="font-mono">Reference protocol: ISO-17025 calibrated detection</span>
                    <Button variant="outline" size="sm" asChild className="h-8 text-xs font-medium">
                      <Link to="/ContactPage">
                        Inquire about custom thresholds
                        <ExternalLink className="size-3.5 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* 2. Certificate of Analysis Retrieval */}
            <AccordionItem value="item-2" className="border-none px-5 md:px-7">
              <AccordionTrigger className="hover:no-underline py-5 text-left group">
                <div className="flex items-center gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-primary border border-border">
                    <FileCheck2 className="size-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-base text-foreground group-hover:text-primary transition-colors">
                      Certificate of Analysis (COA) retrieval & batch traceability
                    </span>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">
                      Lot verification, raw data files, and institutional documentation
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-6">
                <div className="rounded-md bg-muted/60 border border-border p-4 md:p-5">
                  <p className="text-sm text-foreground/90 leading-relaxed mb-4">
                    Each vial shipped features a serialized lot number stamped on both the crimped seal and primary label. Researchers can pull verified certificates containing real chromatograms and mass spectra directly through our portal.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                    {batchTraceabilitySteps.map((step, i) => <div key={step.title} data-index={i} className="p-3.5 rounded-md bg-card border border-border">
                        <div className="font-semibold text-xs md:text-sm text-foreground mb-1">
                          {step.title}
                        </div>
                        <p className="text-xs text-foreground/80 leading-relaxed">
                          {step.desc}
                        </p>
                      </div>)}
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-md bg-card border border-border">
                    <div className="text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">Need historical batch records?</span> Contact support@researchpeptide.in with your order number.
                    </div>
                    <Button variant="secondary" size="sm" asChild className="h-8 text-xs shrink-0">
                      <Link to="/ContactPage">
                        <Download className="size-3.5 mr-1" />
                        Request Batch Archive
                      </Link>
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* 3. Storage, Reconstitution & Transit */}
            <AccordionItem value="item-3" className="border-none px-5 md:px-7">
              <AccordionTrigger className="hover:no-underline py-5 text-left group">
                <div className="flex items-center gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-primary border border-border">
                    <ThermometerSnowflake className="size-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-base text-foreground group-hover:text-primary transition-colors">
                      Lyophilized storage, reconstitution & domestic transit stability
                    </span>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">
                      Temperature stability across India, desiccant sealing, and solvent guidelines
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-6">
                <div className="rounded-md bg-muted/60 border border-border p-4 md:p-5 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="bg-card border border-border p-3.5 rounded-md">
                      <div className="font-mono text-xs text-muted-foreground uppercase mb-1 font-semibold">
                        Lyophilized Powder
                      </div>
                      <div className="text-base font-semibold text-foreground">
                        -20 deg C (long term)
                      </div>
                      <p className="text-xs text-foreground/80 mt-1">
                        Stable at 2 deg C to 8 deg C for up to 30 days in darkness. Protect from ambient moisture.
                      </p>
                    </div>

                    <div className="bg-card border border-border p-3.5 rounded-md">
                      <div className="font-mono text-xs text-muted-foreground uppercase mb-1 font-semibold">
                        Domestic India Transit
                      </div>
                      <div className="text-base font-semibold text-foreground">
                        Ambient / Cold-Pack
                      </div>
                      <p className="text-xs text-foreground/80 mt-1">
                        Lyophilized cakes show no degradation up to 72 hours at 25-30 deg C when desiccant sealed.
                      </p>
                    </div>

                    <div className="bg-card border border-border p-3.5 rounded-md">
                      <div className="font-mono text-xs text-muted-foreground uppercase mb-1 font-semibold">
                        Reconstituted Solution
                      </div>
                      <div className="text-base font-semibold text-foreground">
                        2 deg C to 8 deg C
                      </div>
                      <p className="text-xs text-foreground/80 mt-1">
                        Use bacteriostatic water. Aliquot and store at -80 deg C. Avoid repeated freeze-thaw cycles.
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-md bg-card border border-border text-xs text-foreground/90 space-y-1.5">
                    <div className="font-semibold text-foreground">Reconstitution recommendations:</div>
                    <ul className="list-disc list-inside space-y-1 text-foreground/80">
                      <li>Allow sealed vials to equilibrate to room temperature before opening to avoid condensation.</li>
                      <li>Introduce sterile solvent slowly down the side of the borosilicate vial; do not vortex vigorously.</li>
                      <li>For hydrophobic sequences, brief pre-dissolution in 10-30 uL sterile acetic acid or DMSO is permitted before aqueous dilution.</li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* 4. Research-Use-Only Legal Framework */}
            <AccordionItem value="item-4" className="border-none px-5 md:px-7">
              <AccordionTrigger className="hover:no-underline py-5 text-left group">
                <div className="flex items-center gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-primary border border-border">
                    <Scale className="size-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-base text-foreground group-hover:text-primary transition-colors">
                      Research-Use-Only (RUO) legal framework & restrictions
                    </span>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">
                      Statutory laboratory limits, institutional procurement, and usage compliance
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-6">
                <div className="rounded-md bg-muted/60 border border-border p-4 md:p-5">
                  <div className="flex items-start gap-3 p-3.5 rounded-md bg-destructive/10 border border-destructive/20 mb-4">
                    <AlertCircle className="size-5 text-destructive shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-destructive">
                        Strict laboratory research designation
                      </h3>
                      <p className="text-xs text-foreground/90 mt-1 leading-relaxed">
                        All materials supplied by ResearchPeptide are manufactured and classified strictly for in vitro laboratory research, analytical testing, and academic or industrial experimentation. null of these products are intended for human consumption, clinical diagnosis, therapeutic administration, or veterinary treatment.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-foreground/80">
                    <div className="p-3 rounded-md bg-card border border-border">
                      <div className="font-semibold text-foreground mb-1">Purchaser verification</div>
                      Orders are accepted from recognized universities, pharmaceutical institutes, biotech organizations, and verified private laboratories within India.
                    </div>
                    <div className="p-3 rounded-md bg-card border border-border">
                      <div className="font-semibold text-foreground mb-1">Regulatory compliance</div>
                      Procuring entities must handle reagents according to standard chemical safety protocols and applicable environmental disposal statutes.
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-border flex flex-wrap items-center justify-between gap-3 text-xs">
                    <span className="text-muted-foreground">Detailed safety data sheets (SDS) accompany all outbound consignments.</span>
                    <Button variant="outline" size="sm" asChild className="h-8 text-xs">
                      <Link to="/ContactPage">Institutional Procurement Desk</Link>
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>

        {/* Bottom Contact Strip */}
        <motion.div {...motionProps} className="mt-8 p-4 md:p-5 rounded-md border border-border bg-muted/30 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <h4 className="text-sm font-semibold text-foreground">
              Require specific synthesis limits or custom analytical certificates?
            </h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Our technical staff supports institutional inquiries across all major research centers in India.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" asChild>
              <Link to="/ContactPage">Contact Technical Support</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}